import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, HelpCircle, Compass } from "lucide-react";
import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import FAQAccordion from "../components/FAQAccordion";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { FAQS, GLOSSARY, GLOSSARY_INTRO, RESOURCES_INTRO, HOW_CARECRAFT_HELPS } from "../lib/site";
import { GUIDES, UPCOMING_GUIDE_TOPICS } from "../lib/guides";

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Resources() {
  usePageMeta({
    title: "Resources & FAQ: CareCraft Guides",
    description:
      "Plain-English guides to senior care in Los Angeles, answers to the questions families ask most, and a simple glossary of care terms. CareCraft's guidance is complimentary to families.",
    path: "/resources",
    jsonLd: FAQ_JSONLD,
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "Resources" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>Resources &amp; FAQ</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines
                lines={[
                  <>Helpful information</>,
                  <>for families considering</>,
                  <>the next steps in care</>,
                ]}
              />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                {RESOURCES_INTRO}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite py-20 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="flex items-center gap-3 font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              <BookOpen className="h-7 w-7 text-clay" aria-hidden="true" /> Guides
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {GUIDES.map((g, i) => (
              <Reveal key={g.slug} delay={0.07 * i}>
                <Link
                  to={`/resources/${g.slug}`}
                  data-testid={`resource-page-card-${g.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-inkline bg-ivory p-8 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-forest/35 hover:shadow-[0_16px_40px_rgba(48,50,47,0.08)]"
                >
                  <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-clay">
                    Guide • {g.minutes} min read
                  </p>
                  <h3 className="mt-4 font-serif text-2xl font-medium leading-snug text-forest">
                    {g.title}
                  </h3>
                  <p className="mt-3 flex-1 leading-[1.65] text-charcoal/75">{g.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[0.92rem] font-medium text-forest transition-colors group-hover:text-clay">
                    Read the guide
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.12}>
            <div className="mt-8 rounded-2xl border border-dashed border-inkline p-6">
              <p className="text-[0.85rem] font-medium text-forest">Guides in preparation:</p>
              <p className="mt-2 text-[0.85rem] leading-relaxed text-charcoal/60">
                {UPCOMING_GUIDE_TOPICS.join(" • ")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="flex items-center gap-3 font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              <Compass className="h-7 w-7 text-clay" aria-hidden="true" /> {HOW_CARECRAFT_HELPS.heading}
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-[1.7] text-charcoal/80 md:text-xl">
              {HOW_CARECRAFT_HELPS.statement}
            </p>
            <p className="mt-4 max-w-2xl text-lg leading-[1.7] text-charcoal/80 md:text-xl">
              {HOW_CARECRAFT_HELPS.support}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-warmwhite py-20 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="flex items-center gap-3 font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              <HelpCircle className="h-7 w-7 text-clay" aria-hidden="true" /> Questions families ask
            </h2>
          </Reveal>
          <div className="mt-10 max-w-4xl">
            <FAQAccordion items={FAQS} />
          </div>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              Understanding the care options
            </h2>
            <p className="mt-4 max-w-3xl text-lg leading-[1.7] text-charcoal/75">
              {GLOSSARY_INTRO}
            </p>
          </Reveal>
          <dl className="mt-10 divide-y divide-inkline border-y border-inkline">
            {GLOSSARY.map((g) => (
              <Reveal key={g.term}>
                <div className="grid gap-2 py-6 md:grid-cols-12 md:gap-6">
                  <dt className="font-serif text-xl font-medium text-forest md:col-span-4">{g.term}</dt>
                  <dd className="leading-[1.7] text-charcoal/80 md:col-span-8">{g.def}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
